/**
 * Ask the consultant from anywhere, with the page's context (Arjun, 2026-09-28: "ask copilot should be easy option
 * anywhere and derive context. especially in reports and briefs").
 *
 * A page links to the AI Casting Consultant's page with a question drafted and what it is about:
 *
 *   /app/twin/copilot?ask=<encodeURIComponent(question)>&about=<encodeURIComponent(JSON.stringify(about))>
 *
 * The consultant's page reads both once (askDraftOf, aboutOf), shows "Asking about: <label>" above the composer, and
 * the person's first message carries the context as a block of its own (contextBlockOf). The server checks that block
 * (platform-core/internal/twin/copilot_context.go) and refuses the whole message when it does not check, so aboutOf
 * checks a link's context exactly as the server does, and aboutFor turns what a page knows into a context that checks.
 * Pure.
 */
import type { CopilotContextBlock } from "@/lib/api/copilot-imports";
import { CONSULTANT_PATH, type PanelContent } from "@/lib/copilot/availability";
import type { CopilotAskRequest } from "@/lib/copilot/panel-state";
import { dayNumber, isPlantDay } from "@/lib/plant-days";

/** The pages a question may come from. */
export const ABOUT_PAGES = [
  "brief",
  "report",
  "dashboard",
  "alarms",
  "correlations",
  "batches",
  "equipment",
  "limits",
  "study",
  "actions",
  "acquisition_study",
  "other",
] as const;
export type CopilotAboutPage = (typeof ABOUT_PAGES)[number];

/** A brief's cadence: the overall brief's, a closed period's, or a window someone chose (span). */
export const ABOUT_CADENCES = ["overall", "shift", "day", "week", "month", "year", "span"] as const;
export type CopilotAboutCadence = (typeof ABOUT_CADENCES)[number];

/** Where a question was asked from: the page, and what on it the question is about (contract 2026-09-28). */
export interface CopilotAbout {
  page: CopilotAboutPage;
  cadence?: CopilotAboutCadence;
  /** The brief's period key, required when cadence is set and not "overall". */
  period?: string;
  /** A defect as the briefs key it, e.g. "cold_shut". */
  defect?: string;
  /** A playbook mechanism's id, with the defect it explains: asked about from a mechanism's card. */
  mechanism?: string;
  /** A recommendation's key (platform-core/internal/recommendations): asked about from its card or its row. */
  recommendation?: string;
  /** A condition's key (a driver's condition.key, an excursion's condition.key). */
  condition?: string;
  /** A saved report's id (a uuid). */
  report?: string;
  /** A window's first and last plant days, YYYY-MM-DD, from on or before to. */
  from?: string;
  to?: string;
  /** The passage the person selected, as one line (quoteOf): asked about from a selection (2026-10-06). */
  quote?: string;
  /**
   * An Acquisition study's page (acquisition_study, Acquisition P3c): the study's id, as the page names it, and the point
   * keys of the rows on screen, at most 200. The page sends nothing else of the study: the server reads it itself.
   */
  study_id?: string;
  point_keys?: string[];
  /** What the page calls it, for the chip: one line of 1 to 120 characters. */
  label: string;
}

/** The longest label the chip takes, in characters. */
export const ABOUT_LABEL_MAX = 120;
/** The longest defect or condition key, in characters. */
export const ABOUT_KEY_MAX = 200;
/** The longest window the platform takes, in plant days (a span brief's, rollups_span.go spanMaxDays). */
export const ABOUT_WINDOW_MAX_DAYS = 400;
/** The longest selected passage the context carries, in characters (the server's copilotContextQuote). */
export const ABOUT_QUOTE_MAX = 600;
/** The longest recommendation key, in bytes (recommendations.KeyMax). */
const RECOMMENDATION_MAX_BYTES = 300;
/** The most rows of a study a question carries (the server's copilotStudyKeys), and the longest point key it takes. */
export const ABOUT_POINT_KEYS_MAX = 200;
export const ABOUT_POINT_KEY_MAX = 300;

/** The fields in the contract's order: the order a context is written in, in a link and in a block. */
const FIELDS = [
  "page",
  "cadence",
  "period",
  "defect",
  "mechanism",
  "recommendation",
  "condition",
  "report",
  "from",
  "to",
  "quote",
  "study_id",
  "point_keys",
  "label",
] as const;
type Field = (typeof FIELDS)[number];

/** A period's key by cadence, as plant_briefs keys it (the server's copilotPeriodKeys). */
const PERIOD_KEYS: Record<Exclude<CopilotAboutCadence, "overall">, RegExp> = {
  shift: /^\d{4}-\d{2}-\d{2}\.[A-Za-z0-9_-]{1,32}$/,
  day: /^\d{4}-\d{2}-\d{2}$/,
  week: /^\d{4}-W\d{2}$/,
  month: /^\d{4}-\d{2}$/,
  year: /^\d{4}$/,
  span: /^\d{4}-\d{2}-\d{2}\.\.\d{4}-\d{2}-\d{2}$/,
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** What the server refuses in any one-line text (copilotLineOK): a control or format character, a line or paragraph separator. */
const NOT_ONE_LINE = /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/u;
/** A recommendation's key holds letters, marks, digits and . - _ : @ + only (recommendations.Validate). */
const RECOMMENDATION_CHARS = /^[\p{L}\p{M}\p{Nd}.\-_:@+]+$/u;

/** A text's length in characters (code points), as the server counts it. */
const chars = (s: string): number => [...s].length;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isPage(v: string): v is CopilotAboutPage {
  return (ABOUT_PAGES as readonly string[]).includes(v);
}

function isCadence(v: string): v is CopilotAboutCadence {
  return (ABOUT_CADENCES as readonly string[]).includes(v);
}

/** A defect's, a mechanism's or a condition's key: one line of 1 to 200 characters. */
function isKey(v: string): boolean {
  const n = chars(v);
  return n >= 1 && n <= ABOUT_KEY_MAX && !NOT_ONE_LINE.test(v);
}

/**
 * A recommendation's key as the server takes it (recommendations.Validate): lower case, letters, marks, digits and
 * . - _ : @ + only, "<kind>:<rest>" with both parts, at most 300 bytes (bytes, not characters).
 */
function isRecommendationKey(v: string): boolean {
  if (v === "" || v !== v.toLowerCase() || !RECOMMENDATION_CHARS.test(v)) return false;
  if (new TextEncoder().encode(v).length > RECOMMENDATION_MAX_BYTES) return false;
  const colon = v.indexOf(":");
  return colon > 0 && colon < v.length - 1;
}

/** A selected passage as the server takes it: one line of 1 to 600 characters once trimmed. */
function isQuote(v: string): boolean {
  const n = chars(v.trim());
  return n >= 1 && n <= ABOUT_QUOTE_MAX && !NOT_ONE_LINE.test(v);
}

/** A point's key as the server takes it in a study's context: one line of 1 to 300 characters once trimmed. */
export function isPointKey(v: string): boolean {
  const n = chars(v.trim());
  return n >= 1 && n <= ABOUT_POINT_KEY_MAX && !NOT_ONE_LINE.test(v);
}

/** A study's id as the server takes it: a uuid, in its canonical lower-case form. */
function isStudyId(v: string): boolean {
  return UUID.test(v) && v === v.toLowerCase();
}

/** A cadence and its period: the overall brief's has none; every other cadence has a key of its own form. */
function periodChecks(cadence: CopilotAboutCadence, period: string | undefined): boolean {
  if (cadence === "overall") return period === undefined;
  return period !== undefined && PERIOD_KEYS[cadence].test(period);
}

/** A window of real plant days, the first on or before the last, at most 400 days long. */
export function isAboutWindow(from: string, to: string): boolean {
  return isPlantDay(from) && isPlantDay(to) && from <= to && dayNumber(to) - dayNumber(from) + 1 <= ABOUT_WINDOW_MAX_DAYS;
}

/**
 * A context's fields checked as the server checks them (copilotContext.validate), or null when any does not check:
 *   - page is one of the pages; label is one line of 1 to 120 characters once trimmed (it is kept trimmed);
 *   - a period comes with its cadence, the overall brief has none, and every other cadence has its key's form (a
 *     shift "2026-08-27.1", a day "2026-08-27", a week "2026-W35", a month "2026-08", a year "2026", a span
 *     "2026-08-03..2026-08-31");
 *   - a defect, a mechanism and a condition are one line of 1 to 200 characters, and a mechanism comes with its defect;
 *   - a recommendation is a recommendation's key (lower case, "<kind>:<rest>", at most 300 bytes);
 *   - a quote, the passage selected, is one line of 1 to 600 characters once trimmed (it is kept trimmed);
 *   - a report is a uuid;
 *   - a window has both its days, real plant days, from on or before to, at most 400 days (the server's span limit);
 *   - an Acquisition study's page (acquisition_study) names its study (study_id, a uuid in lower case) and the point keys
 *     of the rows on screen (point_keys: at most 200, each one line of 1 to 300 characters); no other page names either;
 *   - every field is a string (point_keys a list of them), and none is empty: a field the page does not know is left out.
 * Strict (a link's context): a key the contract does not name is malformed too, as the server decodes a block
 * strictly. Otherwise (a stored block, read for the thread) such a key is set aside.
 */
export function aboutFrom(v: unknown, strict = true): CopilotAbout | null {
  if (!isRecord(v)) return null;
  const got: Partial<Record<Exclude<Field, "point_keys">, string>> = {};
  let keys: string[] | undefined;
  for (const [k, value] of Object.entries(v)) {
    if (!(FIELDS as readonly string[]).includes(k)) {
      if (strict) return null;
      continue;
    }
    if (value === undefined) continue;
    if (k === "point_keys") {
      if (!Array.isArray(value) || !value.every((x): x is string => typeof x === "string")) return null;
      keys = value;
      continue;
    }
    if (typeof value !== "string") return null;
    got[k as Exclude<Field, "point_keys">] = value;
  }
  const { page, cadence, period, defect, mechanism, recommendation, condition, report, from, to, quote, study_id } = got;
  if (page === undefined || !isPage(page)) return null;
  const label = got.label?.trim() ?? "";
  if (label === "" || chars(label) > ABOUT_LABEL_MAX || NOT_ONE_LINE.test(got.label ?? "")) return null;
  if (cadence === undefined) {
    if (period !== undefined) return null;
  } else if (!isCadence(cadence) || !periodChecks(cadence, period)) {
    return null;
  }
  if (defect !== undefined && !isKey(defect)) return null;
  if (mechanism !== undefined && (!isKey(mechanism) || defect === undefined)) return null;
  if (recommendation !== undefined && !isRecommendationKey(recommendation)) return null;
  if (condition !== undefined && !isKey(condition)) return null;
  if (report !== undefined && !UUID.test(report)) return null;
  if (quote !== undefined && !isQuote(quote)) return null;
  if ((from === undefined) !== (to === undefined)) return null;
  if (from !== undefined && to !== undefined && !isAboutWindow(from, to)) return null;
  if (page === "acquisition_study") {
    if (study_id === undefined || !isStudyId(study_id)) return null;
    if (keys !== undefined && (keys.length > ABOUT_POINT_KEYS_MAX || !keys.every(isPointKey))) return null;
  } else if (study_id !== undefined || keys !== undefined) {
    return null;
  }
  return ordered({
    page,
    cadence: cadence as CopilotAboutCadence | undefined,
    period,
    defect,
    mechanism,
    recommendation,
    condition,
    report,
    from,
    to,
    quote: quote?.trim(),
    study_id,
    point_keys: keys,
    label,
  });
}

/** A context's fields in the contract's order, each only when it has a value. */
function ordered(a: CopilotAbout): CopilotAbout {
  const out: Record<string, string | string[]> = {};
  for (const f of FIELDS) {
    const value = a[f];
    if (value !== undefined) out[f] = value;
  }
  return out as unknown as CopilotAbout;
}

/** The context a link carries (`?about=`), checked (aboutFrom, strict), or null when there is none or it is malformed. */
export function aboutOf(params: URLSearchParams): CopilotAbout | null {
  const raw = params.get("about");
  if (raw === null) return null;
  try {
    return aboutFrom(JSON.parse(raw), true);
  } catch {
    return null;
  }
}

/**
 * The consultant page's address once it has read a link: without its question and context, so a reload or Back does
 * not draft them again.
 */
export function withoutAskLink(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete("ask");
  next.delete("about");
  return next;
}

/**
 * The link that opens the consultant's page with a question drafted and, when the page knows it, what it is about.
 * The question alone is the link the Plant brief always had; an empty question drafts nothing (the top bar's button:
 * the chip alone).
 */
export function askHref(question: string, about?: CopilotAbout | null): string {
  const params: string[] = [];
  if (question.trim() !== "") params.push(`ask=${encodeURIComponent(question)}`);
  if (about) params.push(`about=${encodeURIComponent(JSON.stringify(ordered(about)))}`);
  return params.length > 0 ? `${CONSULTANT_PATH}?${params.join("&")}` : CONSULTANT_PATH;
}

/** The block a person's message carries for its context (the JSON form's blocks, beside an answer). */
export function contextBlockOf(about: CopilotAbout): CopilotContextBlock {
  return { type: "context", ...ordered(about) };
}

/** What a page knows about where a question comes from, before aboutFor checks it. */
export interface AboutInput {
  page: CopilotAboutPage;
  cadence?: CopilotAboutCadence | null;
  period?: string | null;
  defect?: string | null;
  condition?: string | null;
  report?: string | null;
  from?: string | null;
  to?: string | null;
  /** The page's words for it; one line is made of it, clipped to 120 characters. */
  label: string;
}

/** The chip's words where a page's own are blank. */
const PAGE_LABEL: Record<CopilotAboutPage, string> = {
  brief: "Plant brief",
  report: "Report",
  dashboard: "Dashboard",
  alarms: "Alarms",
  correlations: "Correlations",
  batches: "Batches",
  equipment: "Equipment",
  limits: "Plant limits",
  study: "Study",
  actions: "Action list",
  acquisition_study: "Acquisition study",
  other: "This page",
};

/** A label as the chip takes it: one line, spaces collapsed, at most 120 characters (the last an ellipsis when cut). */
export function aboutLabel(words: string, page: CopilotAboutPage = "other"): string {
  const line = words.replace(/\p{Cf}/gu, "").replace(/[\p{Cc}\s]+/gu, " ").trim();
  if (line === "") return PAGE_LABEL[page];
  const all = [...line];
  return all.length <= ABOUT_LABEL_MAX ? line : `${all.slice(0, ABOUT_LABEL_MAX - 1).join("").trimEnd()}…`;
}

/**
 * What a page knows, as a context that checks (aboutFrom): its label made one line of at most 120 characters, and
 * each other field kept only when it checks. A field that does not (an empty one, a period that is not its cadence's,
 * a window longer than the platform takes, a report id that is not a uuid) is left out rather than sent: the server
 * refuses a message whose context does not check, and the question is worth more than the detail.
 */
export function aboutFor(input: AboutInput): CopilotAbout {
  const out: CopilotAbout = { page: input.page, label: aboutLabel(input.label, input.page) };
  const cadence = input.cadence ?? undefined;
  const period = input.period ?? undefined;
  // The overall brief has no period of its own ("all" names it elsewhere): its cadence alone is kept.
  if (cadence === "overall") out.cadence = cadence;
  else if (cadence !== undefined && periodChecks(cadence, period)) {
    out.cadence = cadence;
    out.period = period;
  }
  if (input.defect && isKey(input.defect)) out.defect = input.defect;
  if (input.condition && isKey(input.condition)) out.condition = input.condition;
  if (input.report && UUID.test(input.report)) out.report = input.report;
  if (input.from && input.to && isAboutWindow(input.from, input.to)) {
    out.from = input.from;
    out.to = input.to;
  }
  return ordered(out);
}

/** "Plant brief: Cold lap": a page's words for itself, and the thing on it a question is about. */
export function itemLabel(page: string, item?: string | null): string {
  return item ? `${page}: ${item}` : page;
}

/** The page a person is on, as it told the copilot (lib/copilot/store PageCopilotContext). */
export interface PageAbout {
  route: string;
  about?: CopilotAbout;
}

/**
 * Where the top bar's button opens the consultant: with the context of the page the person is on, so the chip says
 * where they were; the plain page where it has none. A page that publishes no context leaves the last page's behind
 * (the store keeps it until the next page sets its own), so a context counts only when its route is the page's path.
 */
export function launcherHref(page: PageAbout | null | undefined, pathname: string): string {
  const about = pageAboutOf(page, pathname);
  return about ? askHref("", about) : CONSULTANT_PATH;
}

/**
 * What the page the person is on says it is about, or null. A page that publishes no context leaves the last page's
 * behind (the store keeps it until the next page sets its own), so a context counts only when its route is the path
 * the person is on. The top bar's button and the selection chip both read it.
 */
export function pageAboutOf(page: PageAbout | null | undefined, pathname: string): CopilotAbout | null {
  const route = page?.route.split(/[?#]/, 1)[0].replace(/\/+$/, "") ?? "";
  const here = pathname.replace(/\/+$/, "");
  return page?.about && route === here ? page.about : null;
}

/**
 * What a selection is asked about: the page's own context when it published one for this path (pageAboutOf), else the
 * page as "other", named by its own heading (Arjun, 2026-10-23: the chip was missing on every page that published no
 * context, the newer ones most of all). So the chip works on every page, and a page's richer context still wins.
 */
export function selectionAboutOf(
  page: PageAbout | null | undefined,
  pathname: string,
  heading: string | null | undefined,
): CopilotAbout {
  return pageAboutOf(page, pathname) ?? aboutFor({ page: "other", label: heading ?? "" });
}

/**
 * A link's click, where the consultant opens in the panel: a plain click asks the panel (the browser does not follow
 * the link), and one the browser keeps for opening the page (a new tab, a new window) is left alone, so the link still
 * works as a link. Whether it asked.
 */
export function openInPanel(
  e: Parameters<typeof isPlainClick>[0] & { preventDefault(): void },
  ask: (request: CopilotAskRequest) => void,
  request: CopilotAskRequest,
): boolean {
  if (!isPlainClick(e)) return false;
  e.preventDefault();
  ask(request);
  return true;
}

/** What the top bar's button does. */
export type LauncherAction =
  | { kind: "ask"; request: CopilotAskRequest }
  | { kind: "close" }
  | { kind: "navigate"; href: string };

/**
 * What the top bar's button does: where the consultant's conversation shows in the panel, it opens the panel with the
 * context of the page the person is on (nothing drafted), and closes it when it is open; elsewhere it opens the
 * consultant's page, as it always did (launcherHref).
 */
export function launcherAction(
  content: PanelContent,
  isOpen: boolean,
  page: PageAbout | null | undefined,
  pathname: string,
): LauncherAction {
  if (content !== "conversation") return { kind: "navigate", href: launcherHref(page, pathname) };
  if (isOpen) return { kind: "close" };
  return { kind: "ask", request: { draft: "", about: pageAboutOf(page, pathname) } };
}

/** A click a link keeps for itself: a left click with no modifier key. Any other opens the link in the browser's way. */
export function isPlainClick(e: {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  defaultPrevented: boolean;
}): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.defaultPrevented;
}

/**
 * A selected passage as the context's quote, one line the server takes whatever the browser selected: every format
 * character is dropped (a soft hyphen, a zero-width space, a bidi mark, a byte-order mark: the server refuses them,
 * and they sit inside words, so none leaves a space), as is every control character that is not a line break or a
 * tab; every run of whitespace, in any form (a paragraph break, a tab, a non-breaking space), becomes one space; and
 * it is trimmed. At most 600 characters, the ellipsis included: a longer passage is cut at a word boundary and ends
 * in "…", so a long selection is never refused. "" for a selection of nothing but space.
 */
export function quoteOf(selected: string): string {
  const line = selected
    .replace(/\p{Cf}/gu, "")
    .replace(/(?![\t-\r\u0085])\p{Cc}/gu, "")
    .replace(/[\s\u0085]+/gu, " ")
    .trim();
  const all = [...line];
  if (all.length <= ABOUT_QUOTE_MAX) return line;
  const head = all.slice(0, ABOUT_QUOTE_MAX - 1);
  // The passage's next character is a space when the cut falls between words; else back up to the last space.
  if (all[ABOUT_QUOTE_MAX - 1] !== " ") {
    const at = head.lastIndexOf(" ");
    if (at > 0) head.length = at;
  }
  return `${head.join("").trimEnd()}…`;
}

/** The shortest a cut quote can be unless the passage ends in a word of more than 200 characters (quoteWasCut). */
const CUT_QUOTE_MIN = ABOUT_QUOTE_MAX - 200;

/**
 * Whether a quote is one quoteOf cut: it ends in the ellipsis and is nearly the longest the context takes. The quote
 * is all the chip has of the selection, and a passage cut at a word boundary of 600 characters is never much shorter;
 * a short passage that ends in an ellipsis of its own is not one that was cut.
 */
export function quoteWasCut(quote: string): boolean {
  return quote.endsWith("…") && chars(quote) >= CUT_QUOTE_MIN;
}

/** The keys of the card a selection sits in (data-ask-* on the page's cards, lib/copilot/selection cardKeysOf). */
export interface SelectionKeys {
  defect?: string;
  mechanism?: string;
  recommendation?: string;
  condition?: string;
}

/**
 * The context of a question about a selection: the page's own context, with the selected passage as its quote and the
 * card's keys added. The chip keeps the page's label. A key of the card replaces the page's (a card on a page about
 * another defect is about its own); a key that does not check is left out, and so is a mechanism with no defect, rather
 * than send a context the server refuses. A selection of nothing but space adds no quote.
 */
export function aboutWithSelection(page: CopilotAbout, selected: string, keys: SelectionKeys = {}): CopilotAbout {
  const quote = quoteOf(selected);
  const out: CopilotAbout = { ...page };
  delete out.quote;
  if (quote !== "") out.quote = quote;
  // A card's defect or condition is the subject, in place of the page's: a limits row's defect never stands beside the
  // address's condition, nor a condition row's beside the page's defect (#1186 review). A card's mechanism or
  // recommendation alone still reads with the page's defect; a selection in no card keeps the page's keys.
  if (keys.defect !== undefined) {
    delete out.condition;
    delete out.mechanism;
    delete out.recommendation;
  }
  if (keys.condition !== undefined) {
    delete out.defect;
    delete out.mechanism;
    delete out.recommendation;
  }
  if (keys.defect !== undefined && isKey(keys.defect)) out.defect = keys.defect;
  if (keys.condition !== undefined && isKey(keys.condition)) out.condition = keys.condition;
  if (keys.recommendation !== undefined && isRecommendationKey(keys.recommendation)) out.recommendation = keys.recommendation;
  if (keys.mechanism !== undefined && isKey(keys.mechanism)) out.mechanism = keys.mechanism;
  if (out.mechanism !== undefined && out.defect === undefined) delete out.mechanism;
  return ordered(out);
}
